import { between, stepParticles, type Particle } from './particle-field.js';

/**
 * Sparks and dust for the stone door. Both are thrown from the two inner edges of the slabs while they move: sparks where
 * stone scrapes stone, dust where it shakes loose. The host reports where the edges are each frame, so the effect follows the slabs.
 */
export class GateParticles {
  private readonly canvas = document.createElement('canvas');
  private readonly context = this.canvas.getContext('2d')!;
  private particles: Particle[] = [];
  private last = performance.now();
  private previous: [number, number] | null = null;
  private frame = 0;
  private emitting = true;

  constructor(private host: HTMLElement, private edges: () => [number, number], private done: () => void) {
    this.canvas.className = 'gate-particles'; this.canvas.setAttribute('aria-hidden', 'true');
    host.append(this.canvas); this.resize();
    this.frame = requestAnimationFrame(() => this.tick());
  }

  /** A shower and a cloud from the seam, for the moment the locks let go. */
  burst(sparks: number, clouds: number) {
    const [left, right] = this.edges(), x = (left + right) / 2, height = this.canvas.clientHeight;
    for (let i = 0; i < sparks; i++) this.spark(x, between(height * .2, height * .8), between(-1, 1) > 0 ? 1 : -1, 1.4);
    for (let i = 0; i < clouds; i++) this.dust(x, between(0, height), between(-1, 1), 1.3);
  }

  /** No more is thrown; the canvas goes once what is in the air has settled. */
  finish() { this.emitting = false; }

  private resize() {
    const ratio = Math.min(devicePixelRatio, 2);
    this.canvas.width = this.host.clientWidth * ratio; this.canvas.height = this.host.clientHeight * ratio;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  /** `side` is +1 for sparks leaving the left slab's edge into the opening, -1 for the right slab's. */
  private spark(x: number, y: number, side: number, power = 1) {
    this.particles.push({ kind: 'spark', x, y, vx: side * between(60, 360) * power, vy: between(-420, 60) * power, age: 0, life: between(.3, .85), size: between(1, 2.4) });
  }
  private dust(x: number, y: number, side: number, power = 1) {
    this.particles.push({ kind: 'dust', x, y, vx: side * between(8, 55) * power, vy: between(-34, 6), age: 0, life: between(1.6, 3.2), size: between(36, 110) * Math.min(power, 1.3) });
  }

  private tick() {
    const now = performance.now(), dt = Math.max(0, Math.min(.05, (now - this.last) / 1000)); this.last = now;
    const { clientWidth: width, clientHeight: height } = this.canvas;
    if (this.canvas.width !== width * Math.min(devicePixelRatio, 2)) this.resize();
    const edges = this.edges();
    if (this.emitting && this.previous && dt > 0) {
      // The faster an edge is moving, the more it grinds; the left slab's edge throws toward the right and vice versa.
      const speeds = [Math.abs(edges[0] - this.previous[0]) / dt, Math.abs(edges[1] - this.previous[1]) / dt];
      ([[edges[0], speeds[0], 1], [edges[1], speeds[1], -1]] as const).forEach(([x, speed, side]) => {
        const moving = Math.min(1, speed / 300);
        if (x < -20 || x > width + 20) return;
        for (let n = moving * dt * 260; n > 0; n--) if (Math.random() < Math.min(1, n)) this.spark(x, between(0, height), side);
        for (let n = moving * dt * 70; n > 0; n--) if (Math.random() < Math.min(1, n)) this.dust(x, between(0, height), side);
      });
    }
    this.previous = edges;
    this.particles = stepParticles(this.context, this.particles, dt, width, height);
    if (!this.emitting && !this.particles.length) { this.canvas.remove(); this.done(); return; }
    this.frame = requestAnimationFrame(() => this.tick());
  }

  dispose() { cancelAnimationFrame(this.frame); this.canvas.remove(); }
}
